import React, { useState } from 'react';
import {
  Upload, Button, Table, Tag, Alert, Statistic, Row, Col, Card, Space, Modal, Divider,
} from 'antd';
import {
  FileExcelOutlined, CloudUploadOutlined, CheckCircleOutlined, WarningOutlined,
} from '@ant-design/icons';
import { XLSX } from '../../utils/xlsxShim';
import { extraireLignes } from './parseEtatReclamations';
import Layout from '../../components/Layout';
import axiosInstance from '../../variables/api/axiosInstance';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { localeIntl } from '../../i18n/format';
import PageHeader from '../../components/PageHeader';

const { Dragger } = Upload;

/* ── Composant ──────────────────────────────────────────────────────────── */

export default function RepriseEtatReclamations() {
  const { t } = useTranslation('reprise');
  const [lignes, setLignes] = useState([]);
  const [rapport, setRapport] = useState(null); // resultat dry-run
  const [loading, setLoading] = useState(false);

  const onExcelDrop = async (file) => {
    setRapport(null);
    try {
      const buf = await file.arrayBuffer();
      const wb = await XLSX.read(buf, { cellDates: true });
      const { lignes: extraites, feuillesLues, feuillesSansEntete } = extraireLignes(wb);
      if (!extraites.length) {
        toast.error(t('etatReclamations.aucuneLigne'));
        return false;
      }
      const detail = feuillesLues.map((f) => `${f.nom.trim()} (${f.nb})`).join(', ');
      toast.success(t('etatReclamations.lignesExtraites', { nombre: extraites.length, detail }));
      if (feuillesSansEntete.length) {
        toast.warn(t('etatReclamations.feuillesIgnorees', { feuilles: feuillesSansEntete.join(', ') }));
      }
      setLignes(extraites);
      await lancerRapprochement(extraites, true); // dry-run automatique
    } catch (e) {
      toast.error(t('etatReclamations.lectureImpossible', { message: e.message }));
    }
    return false; // empeche l'upload auto d'AntD
  };

  const lancerRapprochement = async (data, dryRun) => {
    setLoading(true);
    try {
      const res = await axiosInstance.post(
        `/v1/reprise/etat-reclamations?dryRun=${dryRun}`, { lignes: data });
      setRapport(res.data?.data);
      if (!dryRun) { toast.success(t('etatReclamations.repriseAppliquee')); }
    } catch (e) {
      toast.error(e?.response?.data?.message || t('etatReclamations.erreurRapprochement'));
    } finally {
      setLoading(false);
    }
  };

  const confirmerApplication = () => {
    Modal.confirm({
      title: t('etatReclamations.confirmerTitre'),
      icon: <WarningOutlined style={{ color: '#d97706' }} />,
      content: (
        <>
          {t('etatReclamations.confirmerContenu', { nombre: rapport.rapproches })}
        </>
      ),
      okText: t('commun.appliquer'), okButtonProps: { danger: true }, cancelText: t('commun.annuler'),
      onOk: () => lancerRapprochement(lignes, false),
    });
  };

  const badge = (resultat) => {
    const map = {
      RAPPROCHE: { color: 'green', label: t('etatReclamations.resultats.RAPPROCHE') },
      NON_TROUVE: { color: 'red', label: t('etatReclamations.resultats.NON_TROUVE') },
      AMBIGU: { color: 'orange', label: t('etatReclamations.resultats.AMBIGU') },
      ERREUR: { color: 'volcano', label: t('etatReclamations.resultats.ERREUR') },
    };
    const m = map[resultat] || { color: 'default', label: resultat };
    return <Tag color={m.color}>{m.label}</Tag>;
  };

  const cols = [
    { title: '', dataIndex: 'resultat', width: 110, render: badge },
    { title: t('etatReclamations.colonnes.sinBncbTg'), dataIndex: 'sinBncbTg', width: 130 },
    { title: t('etatReclamations.colonnes.compagnie'), dataIndex: 'compagnie', width: 110 },
    {
      title: t('etatReclamations.colonnes.sinistreTrouve'), dataIndex: 'numeroSinistreTrouve', width: 140,
      render: (v, r) => v ? <span>{v} <Tag>{r.strategieRapprochement}</Tag></span> : '-',
    },
    {
      title: t('etatReclamations.colonnes.statut'), key: 'statut', width: 200,
      render: (_, r) => {
        if (!r.statutActuel && !r.statutCible) return '-';
        if (r.statutChange) return <span>{r.statutActuel || '∅'} → <b style={{ color: '#1a6b3c' }}>{r.statutCible}</b></span>;
        return <span style={{ color: '#9ca3af' }}>{r.statutActuel} <i>{t('etatReclamations.inchange')}</i></span>;
      },
    },
    {
      title: t('etatReclamations.colonnes.montantBap'), dataIndex: 'montantBap', width: 120, align: 'right',
      render: (v) => v != null ? Number(v).toLocaleString(localeIntl()) : '-',
    },
    { title: t('etatReclamations.colonnes.exercice'), dataIndex: 'exerciceBap', width: 80, align: 'center', render: (v) => v || '-' },
    { title: t('etatReclamations.colonnes.detail'), dataIndex: 'raisonStatutInchange', ellipsis: true, render: (v, r) => v || r.message || '' },
  ];

  const tauxRappro = rapport && rapport.total
    ? Math.round((100 * rapport.rapproches) / rapport.total) : 0;

  return (
    <Layout title={t('etatReclamations.titreOnglet')}>
      {/* Assistant d'import : pas de `compte`, l'ecran demarre vide et un
          « 0 reclamation » se lirait comme une liste vide. */}
      <PageHeader
        titre={t('etatReclamations.titre')}
        icone={<CloudUploadOutlined />}
      />

      <Card style={{ marginBottom: 16 }}>
        <Dragger accept=".xlsx" beforeUpload={onExcelDrop} showUploadList={false} disabled={loading}>
          <p className="ant-upload-drag-icon"><FileExcelOutlined style={{ color: '#1a6b3c' }} /></p>
          <p className="ant-upload-text">{t('etatReclamations.glissez')}</p>
          <p className="ant-upload-hint">
            {t('etatReclamations.aideFichier')}
          </p>
        </Dragger>
      </Card>

      {rapport && (
        <>
          <Card style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col span={4}><Statistic title={t('etatReclamations.stats.lignes')} value={rapport.total} /></Col>
              <Col span={4}><Statistic title={t('etatReclamations.stats.rapproches')} value={rapport.rapproches}
                valueStyle={{ color: '#1a6b3c' }} suffix={`/ ${rapport.total}`} /></Col>
              <Col span={4}><Statistic title={t('etatReclamations.stats.taux')} value={tauxRappro} suffix="%" /></Col>
              <Col span={4}><Statistic title={t('etatReclamations.stats.nonTrouves')} value={rapport.nonTrouves}
                valueStyle={{ color: rapport.nonTrouves ? '#dc2626' : undefined }} /></Col>
              <Col span={4}><Statistic title={t('etatReclamations.stats.statutsAMettreAJour')} value={rapport.statutsMisAJour} /></Col>
              <Col span={4}><Statistic title={t('etatReclamations.stats.bapRepris')} value={rapport.bapRepris} /></Col>
            </Row>
            <Divider />
            {rapport.dryRun ? (
              <Space>
                <Alert type="info" showIcon style={{ flex: 1 }}
                  message={t('etatReclamations.simulation')} />
                <Button type="primary" danger onClick={confirmerApplication} disabled={!rapport.rapproches}
                  icon={<CloudUploadOutlined />}>
                  {t('etatReclamations.appliquerNombre', { nombre: rapport.rapproches })}
                </Button>
              </Space>
            ) : (
              <Alert type="success" showIcon icon={<CheckCircleOutlined />}
                message={t('etatReclamations.appliquee')} />
            )}
          </Card>

          <Card>
            <Table
              size="small" rowKey={(r, i) => i} columns={cols} dataSource={rapport.lignes}
              loading={loading}
              pagination={{ pageSize: 25, showSizeChanger: true }}
              rowClassName={(r) => r.resultat === 'NON_TROUVE' ? 'cb-row-warn' : ''}
            />
          </Card>
        </>
      )}
    </Layout>
  );
}
